import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Pencil, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const DUE_WINDOW = 30;
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  if (isNaN(d.getTime())) return null;
  return Math.round((d - today) / 86400000);
}
function CollegeDueBadge({ dateStr }) {
  const d = daysUntil(dateStr);
  if (d === null || d < 0 || d > DUE_WINDOW) return null;
  const label = d === 0 ? "Today" : `in ${d}d`;
  const cls = d <= 7 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-800";
  return <span data-testid="college-due-badge" className={`ml-2 text-[11px] font-semibold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
}

const PHASES = ["1", "2", "3", "4", "5", "6", "7"];
const STATUS_OPTS = [
  { v: "completed", label: "Completed" },
  { v: "in_progress", label: "In progress" },
  { v: "not_yet", label: "Not yet" },
];
const STATUS_CLS = {
  completed: "bg-emerald-100 text-emerald-700",
  in_progress: "bg-amber-100 text-amber-800",
  not_yet: "bg-zinc-100 text-zinc-400",
};
const blankPhases = () => PHASES.reduce((o, p) => ({ ...o, [p]: "not_yet" }), {});
const EMPTY = { employee_id: "", phases: blankPhases(), college: "", next_college_date: "", notes: "" };

export default function Apprentices() {
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.get("/apprentices").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/employees").then((r) => setEmps(r.data)).catch(() => {}); }, []);

  const takenIds = new Set(rows.map((r) => r.employee_id));
  const openNew = () => { setEditId(null); setForm({ ...EMPTY, phases: blankPhases() }); setOpen(true); };
  const openEdit = (a) => {
    setEditId(a.id);
    setForm({ employee_id: a.employee_id, phases: { ...blankPhases(), ...(a.phases || {}) }, college: a.college || "", next_college_date: a.next_college_date || "", notes: a.notes || "" });
    setOpen(true);
  };
  const setPhase = (p, v) => setForm((f) => ({ ...f, phases: { ...f.phases, [p]: v } }));
  const save = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    try {
      if (editId) await api.put(`/apprentices/${editId}`, form);
      else await api.post("/apprentices", form);
      toast.success(editId ? "Apprentice updated" : "Apprentice added"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/apprentices/${delTarget.id}`); toast.success("Apprentice removed"); setDelTarget(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const availableEmps = emps.filter((e) => e.active !== false && (!takenIds.has(e.id) || e.id === form.employee_id));
  const dueSoon = rows.filter((a) => { const d = daysUntil(a.next_college_date); return d !== null && d >= 0 && d <= DUE_WINDOW; });

  return (
    <AdminLayout nav={adminNav} title="Apprentices">
      <PageHeader title="Apprentices" subtitle={`${rows.length} apprentice${rows.length === 1 ? "" : "s"} · Irish electrical apprenticeship phases 1–7`}>
        <Button data-testid="new-apprentice-btn" onClick={openNew} className="bg-slate-900 text-white">+ Add apprentice</Button>
      </PageHeader>

      {dueSoon.length > 0 && (
        <div data-testid="college-reminder-banner" className="mb-4 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <CalendarClock className="h-4 w-4 shrink-0 mt-0.5" />
          <span><span className="font-semibold">{dueSoon.length}</span> apprentice{dueSoon.length === 1 ? "" : "s"} due at college within {DUE_WINDOW} days — {dueSoon.map((a) => a.employee_name).join(", ")}</span>
        </div>
      )}

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Apprentice</th><th className="p-3">Phases 1–7</th><th className="p-3">Current</th><th className="p-3">College</th><th className="p-3">Next college</th><th className="p-3">Notes</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} data-testid="apprentices-empty" className="p-6 text-center text-zinc-400">No apprentices yet. Add one to start tracking phases.</td></tr>}
            {rows.map((a) => (
              <tr key={a.id} data-testid={`apprentice-row-${a.id}`} className="border-b border-zinc-50 hover:bg-zinc-50 align-top">
                <td className="p-3 font-medium whitespace-nowrap">{a.employee_name}<div className="text-xs text-zinc-400 font-normal">{a.employee_number}</div></td>
                <td className="p-3">
                  <div className="flex gap-1">
                    {PHASES.map((p) => (
                      <span key={p} title={`Phase ${p}: ${(a.phases?.[p] || "not_yet").replace("_", " ")}`} className={`h-6 w-6 rounded flex items-center justify-center text-[11px] font-bold ${STATUS_CLS[a.phases?.[p] || "not_yet"]}`}>{p}</span>
                    ))}
                  </div>
                </td>
                <td className="p-3 whitespace-nowrap">{a.current_phase ? <span className="text-xs font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">Phase {a.current_phase}</span> : <span className="text-zinc-400 text-xs">—</span>}</td>
                <td className="p-3 text-zinc-600 whitespace-nowrap">{a.college || "—"}</td>
                <td className="p-3 num whitespace-nowrap">{a.next_college_date ? <>{fmtDate(a.next_college_date)}<CollegeDueBadge dateStr={a.next_college_date} /></> : "—"}</td>
                <td className="p-3 text-zinc-500 max-w-[220px]">{a.notes || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button data-testid={`appr-edit-${a.id}`} size="sm" variant="outline" className="h-8 mr-1" onClick={() => openEdit(a)}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button data-testid={`appr-del-${a.id}`} size="sm" variant="outline" className="h-8 text-rose-600 border-rose-200" onClick={() => setDelTarget(a)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap gap-3 text-xs text-zinc-500">
        <span className="flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded bg-emerald-100 border border-emerald-200" /> Completed</span>
        <span className="flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded bg-amber-100 border border-amber-200" /> In progress (current phase)</span>
        <span className="flex items-center gap-1.5"><span className="h-3.5 w-3.5 rounded bg-zinc-100 border border-zinc-200" /> Not yet</span>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto kqm-scroll">
          <DialogHeader><DialogTitle>{editId ? "Edit apprentice" : "Add apprentice"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="appr-employee" className="mt-1"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{availableEmps.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.surname}{e.employee_number ? ` (${e.employee_number})` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Phases</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {PHASES.map((p) => (
                  <div key={p} className="flex items-center gap-2">
                    <span className="text-xs font-bold text-zinc-500 w-14 shrink-0">Phase {p}</span>
                    <Select value={form.phases[p]} onValueChange={(v) => setPhase(p, v)}>
                      <SelectTrigger data-testid={`appr-phase-${p}`} className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>College</Label><Input data-testid="appr-college" value={form.college} onChange={(e) => setForm({ ...form, college: e.target.value })} className="mt-1" /></div>
              <div><Label>Next college date</Label><Input data-testid="appr-next-date" type="date" value={form.next_college_date || ""} onChange={(e) => setForm({ ...form, next_college_date: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label>Notes</Label><textarea data-testid="appr-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" /></div>
          </div>
          <Button data-testid="appr-save" onClick={save} className="w-full bg-slate-900 text-white mt-2">{editId ? "Save changes" : "Add apprentice"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Remove apprentice?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500">This removes the apprenticeship record for {delTarget?.employee_name}. Their employee profile is not affected.</p>
          <div className="flex justify-end gap-2 mt-3">
            <Button variant="outline" data-testid="appr-del-cancel" onClick={() => setDelTarget(null)}>Cancel</Button>
            <Button data-testid="appr-del-confirm" onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white">Remove</Button>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
